/**
 * =============================================================================
 * IDIOMAS Y NAMESPACES DEL PORTAL REVI
 * =============================================================================
 * Copia deliberada del montaje que ya funciona en Educa360-Registro (K12), con
 * UNA diferencia que importa.
 *
 * EN K12 EL SELECTOR SE APAGO A MANO. Su `i18n.js` fuerza `lng` al idioma por
 * defecto con un comentario que dice "hasta que se revisen las traducciones".
 * Resultado: 1.367 claves inglesas terminadas desde marzo de 2026 y el selector
 * apagado desde abril. Apagarlo es una linea; volver a encenderlo obliga a
 * tocar la inicializacion, y por eso nadie lo hace.
 *
 * AQUI SE HACE AL REVES: cada idioma lleva su propio interruptor. i18next
 * conoce y sabe cargar todos; el selector solo ofrece los que estan encendidos.
 * Encender el frances el dia que haya revisor es cambiar `enabled: false` por
 * `true` en una linea de este fichero, sin tocar nada mas.
 *
 * LA REGLA QUE SOSTIENE ESTO: NINGUN IDIOMA SE ENCIENDE A MEDIAS. Un idioma
 * incompleto no es medio producto, es un producto que parece descuidado, y
 * ademas no se nota: i18next cae al castellano, asi que las pantallas que se
 * miran se ven bien y las que no, no.
 *
 * Eso lo vigila un test: para CADA idioma encendido comprueba que tiene todas
 * las claves del castellano y ninguna vacia. Es mejor guardia que el que habia
 * antes ("el frances tiene que estar apagado"), porque no caduca en cuanto se
 * toma la decision de encenderlo.
 * =============================================================================
 */

export const SUPPORTED_LANGUAGES = [
  {
    code: "es",
    label: "Español",
    antdLocale: "es_ES",
    enabled: true,
  },
  {
    code: "fr",
    label: "Français",
    antdLocale: "fr_FR",
    // ENCENDIDO el 2026-08-09 por decision de Antonio, SIN revision nativa
    // previa: no hay revisores disponibles y hace falta ver como queda en
    // producción.
    //
    // Lo que eso significa, dicho claro: el frances esta completo y el
    // vocabulario esta razonado en docs/revi/i18n/GLOSARIO_FR.md, pero nadie
    // del sector medico-social frances lo ha leido. Lo que puede fallar no es
    // la traduccion, son las ELECCIONES DE TERMINO. La primera candidata a
    // cambiar es "patient" frente a "usager", que en Francia tiene carga
    // politica. Las otras tres estan al final del glosario.
    enabled: true,
  },
];

export const DEFAULT_LANGUAGE = "es";
export const FALLBACK_LANGUAGE = "es";

/**
 * Namespaces por ROL, no por pantalla. Sale del reparto medido del portal: el
 * terapeuta concentra el 41% del texto y el admin el 29%. Partirlo asi permite
 * cargar solo lo que usa cada uno y, sobre todo, traducir por prioridad de
 * negocio en vez de por orden de carpetas.
 */
export const NAMESPACES = [
  "common", // navegacion, acciones, estados: lo comparte todo el portal
  "auth", // acceso, registro, recuperar contrasena
  "therapist", // paginas del terapeuta
  "therapy", // construir e impartir una terapia
  "motricity", // juegos de motricidad, graficas y video en directo
  "evaluations", // interfaz de las evaluaciones clinicas (NO sus enunciados)
  "rooms", // salas y escenas
  "institution", // portal de la institucion
  "patient", // portal del paciente
  "ui", // piezas compartidas de interfaz
  "misc", // lo que no encaja en ninguno de los anteriores
];

// UN NAMESPACE POR AREA, y no uno gigante por rol. Ademas de cargar menos por
// pantalla, es lo que permite repartir la extraccion entre varias manos sin que
// dos escriban nunca el mismo fichero: cada area tiene un dueno.

/**
 * LO QUE NO ENTRA AQUI, Y POR QUE:
 *
 * - El PANEL DE ADMINISTRACION de REVI no vive en este portal, sino en el
 *   workspace `client/` (/admin/revi). No es alcance de este milestone.
 * - La LANDING PUBLICA tiene su propio sistema, hecho a mano y ya en produccion
 *   con ES/EN/FR. Decision de Antonio: no se migra de momento.
 * - Los ENUNCIADOS de las 14 escalas clinicas validadas. Traducirlos no las hace
 *   validas y dos tienen licencia de terceros. Se traduce la interfaz que las
 *   rodea, no sus items.
 * - EL TEXTO QUE ESCRIBE EL TERAPEUTA: sus terapias, sus evaluaciones propias,
 *   sus notas de sesion y la anamnesis de un paciente. Es contenido clinico
 *   suyo, en su idioma, y traducirlo automaticamente seria alterar una historia
 *   clinica.
 * - El CONTENIDO DE LAS ESCENAS VR no se traduce aqui: se traduce como DATO, en
 *   el campo `translations` del documento de escena, que comparten REVI y K12.
 *   Ver docs/revi/i18n/AUDITORIA_I18N_PORTAL.md
 */

export const DEFAULT_NAMESPACE = "common";

/** Idiomas que se le ofrecen de verdad al usuario. */
export function getEnabledLanguages() {
  return SUPPORTED_LANGUAGES.filter((l) => l.enabled);
}

export function isLanguageEnabled(code) {
  return getEnabledLanguages().some((l) => l.code === code);
}

/**
 * Resuelve a que idioma arrancar.
 *
 * Un idioma persistido en el navegador que despues se apaga NO puede
 * sobrevivir: si alguien probo el frances mientras estuvo encendido y luego se
 * apaga, su navegador seguiria pidiendolo y veria un idioma que hemos retirado.
 */
export function resolveInitialLanguage(persisted) {
  return isLanguageEnabled(persisted) ? persisted : DEFAULT_LANGUAGE;
}
